import * as React from 'react'

// `id` is widened to `string | number` so the same selection-set primitive can
// hold either numeric IDs (drivers, vehicles — long PKs in tenant DB) or UUID
// strings (companies — Guid PK in central admin DB).
export type SelectedItem<T = unknown> = {
  id: string | number
  isActive?: boolean
  additionalType?: T
}

export type SelectionGroup<T = unknown> =
  | { ids: (string | number)[]; isActive?: boolean; additionalType?: T }
  | null
type TableContextType<T = unknown> = {
  selectedGroup: SelectionGroup<T>
  allIds: SelectedItem<T>[]
  setAllIds: (ids: SelectedItem<T>[]) => void
  toggleId: (id: string | number, isActive?: boolean, additionalType?: T) => void
  toggleAll: (checked: boolean, isActive?: boolean, additionalType?: T) => void
  resetAll: () => void
  retainIds: (allowed: (string | number)[]) => void
  isSelectedAny: boolean
  isActiveGroup: boolean | undefined
}

const TableContext = React.createContext<TableContextType<any> | null>(null)

export const TableProvider = <T,>({
  children,
  defaultSelected = [],
  bulk = true,
}: {
  children: React.ReactNode
  bulk?: boolean
  defaultSelected?: number[]
}) => {
  const [selectedGroup, setSelectedGroup] = React.useState<SelectionGroup>(null)
  const [allIds, setAllIds] = React.useState<SelectedItem[]>([])
  const hasActiveField = React.useMemo(
    () => allIds.some((item) => typeof item.isActive !== 'undefined'),
    [allIds],
  )

const toggleId = (id: number, isActive?: boolean, additionalType?: T) => {
  setSelectedGroup((prev) => {
    if (prev === null) {
      return { ids: [id], isActive, additionalType }
    }

    if (hasActiveField && prev.isActive !== isActive) {
      console.warn(
        `Nie można wybrać elementu o isActive=${isActive} do grupy o isActive=${prev.isActive}`,
      )
      return prev
    }

    if (!bulk) {
      const alreadySelected = prev.ids.includes(id)
      if (alreadySelected) return null
      return { ids: [id], isActive, additionalType }
    }

    if (prev.ids.includes(id)) {
      const newIds = prev.ids.filter((x) => x !== id)
      return newIds.length > 0 ? { ...prev, ids: newIds } : null
    }

    return { ...prev, ids: [...prev.ids, id] }
  })
}

  const toggleAll = (checked: boolean, isActive?: boolean) => {
    if (!bulk) {
      resetAll()
      return
    }

    if (checked) {
      let filteredIds: number[] = []

      if (hasActiveField) {
        filteredIds = allIds
          .filter((item) => (item.isActive ?? false) === isActive)
          .map((item) => item.id)
      } else {
        filteredIds = allIds.map((item) => item.id)
      }

      setSelectedGroup(
        filteredIds.length > 0
          ? hasActiveField
            ? { ids: filteredIds, isActive }
            : { ids: filteredIds }
          : null,
      )
    } else {
      setSelectedGroup(null)
    }
  }

  const resetAll = () => setSelectedGroup(null)

  const retainIds = React.useCallback((allowed: (string | number)[]) => {
    setSelectedGroup((prev) => {
      if (prev === null) return prev
      const allowedSet = new Set(allowed)
      const ids = prev.ids.filter((id) => allowedSet.has(id))
      if (ids.length === prev.ids.length) return prev
      return ids.length > 0 ? { ...prev, ids } : null
    })
  }, [])

  const initializedRef = React.useRef(false)

  React.useEffect(() => {
    if (
      defaultSelected &&
      defaultSelected.length > 0 &&
      allIds.length > 0 &&
      !initializedRef.current
    ) {
      initializedRef.current = true
      setSelectedGroup({
        ids: defaultSelected,
        isActive: hasActiveField
          ? allIds.find((item) => item.id === defaultSelected[0])?.isActive
          : undefined,
      })
    }
    if (!defaultSelected || defaultSelected.length === 0) {
      initializedRef.current = false
    }
  }, [defaultSelected, allIds, hasActiveField])
  const isSelectedAny = selectedGroup !== null && selectedGroup.ids.length > 0
  const isActiveGroup = selectedGroup?.isActive
  const value: TableContextType = {
    selectedGroup,
    allIds,
    setAllIds,
    toggleId,
    toggleAll,
    resetAll,
    retainIds,
    isSelectedAny,
    isActiveGroup,
  }

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>
}

export const useTable = (): TableContextType => {
  const context = React.useContext(TableContext)
  if (!context) {
    throw new Error('useTable2 must be used within a TableProvider')
  }
  return context
}
